import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { navLinks, siteMeta } from "../data/mock";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const location = useLocation();

  const { scrollY } = useScroll();

  // Lock scroll when menu is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    if (latest > previous && latest > 150) {
      setVisible(false);
    } else {
      setVisible(true);
    }
    setScrolled(latest > 40);
  });

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: visible ? 0 : -100,
          opacity: visible ? 1 : 0
        }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          backdropFilter: scrolled ? "blur(18px)" : "none",
          background: scrolled ? "rgba(10,9,8,0.72)" : "transparent",
          borderBottom: scrolled ? "1px solid rgba(201,169,97,0.12)" : "1px solid transparent",
          transition: "background 0.4s ease, border-color 0.4s ease, backdrop-filter 0.4s ease"
        }}
      >
        <div className="container-xl flex items-center justify-between py-5">
          <Link to="/" className="flex items-center" data-cursor="home">
            <Logo />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => {
              const active = isActive(l.href);
              return (
                <div key={l.label} className="relative group">
                  {l.sublinks ? (
                    <>
                      <Link
                        to={l.href}
                        className={`link-reveal text-[13px] uppercase tracking-[0.18em] flex items-center gap-1.5 transition-colors duration-350 ${
                          active ? "text-[var(--gold)]" : "text-[var(--cream-muted)] hover:text-[var(--cream)]"
                        }`}
                      >
                        {l.label}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`group-hover:rotate-180 transition-transform duration-500 ${active ? "opacity-100" : "opacity-60 group-hover:opacity-100"}`}>
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </Link>
                      {/* Dropdown Menu */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300">
                        <div className="bg-[var(--ink)] border border-[var(--rule)] rounded-xl p-4 min-w-[220px] shadow-2xl backdrop-blur-xl">
                          <div className="grid gap-2">
                            {l.sublinks.map((sub) => {
                              const subActive = isActive(sub.href);
                              return (
                                <Link
                                  key={sub.href}
                                  to={sub.href}
                                  className={`block px-4 py-2 text-[11px] uppercase tracking-widest rounded-lg transition-all ${
                                    subActive ? "text-[var(--gold)] bg-white/5" : "text-[var(--cream-muted)] hover:text-[var(--gold)] hover:bg-white/5"
                                  }`}
                                >
                                  {sub.label}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={l.href}
                      className={`link-reveal text-[13px] uppercase tracking-[0.18em] transition-colors duration-350 relative ${
                        active ? "text-[var(--gold)]" : "text-[var(--cream-muted)] hover:text-[var(--cream)]"
                      }`}
                    >
                      {l.label}
                      {active && (
                        <motion.div 
                          layoutId="activeDot"
                          className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[var(--gold)]"
                        />
                      )}
                    </Link>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="hidden md:flex">
            <Link to="/contact" className="btn-primary" data-cursor="say hi">
              Start a project
            </Link>
          </div>

          <button
            className="md:hidden text-[var(--cream)] p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
            className="fixed inset-0 z-[60] bg-[#0a0a0a] flex flex-col"
          >
            {/* Sidebar Top Bar - Fixed Header */}
            <div className="w-full px-6 py-2 flex items-center justify-between border-b border-white/5 bg-[#0a0a0a]">
              <Link to="/" onClick={() => setOpen(false)} className="scale-90 origin-left">
                <Logo />
              </Link>
              <button
                onClick={() => setOpen(false)}
                className="w-12 h-12 flex items-center justify-center rounded-full border border-white/10 text-[var(--gold)] hover:bg-white/5 transition-all duration-300"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            {/* Main Navigation Body */}
            <div className="flex-1 px-6 py-12 flex flex-col justify-center overflow-y-auto">
              <nav className="space-y-6 pt-10">
                {navLinks.map((l, i) => {
                  const active = isActive(l.href);
                  return (
                    <motion.div
                      key={l.label}
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.1 + i * 0.05 }}
                    >
                      <Link
                        to={l.href}
                        onClick={() => setOpen(false)}
                        className={`font-display text-3xl transition-all duration-300 block leading-tight ${
                          active ? "text-[var(--gold)] italic" : "text-[var(--cream)] hover:text-[var(--gold)]"
                        }`}
                      >
                        {l.label}
                        {active && <span className="ml-2 text-xs not-italic opacity-40">●</span>}
                      </Link>

                      {l.sublinks && (
                        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 opacity-60">
                          {l.sublinks.map((sub) => {
                            const subActive = isActive(sub.href);
                            return (
                              <Link
                                key={sub.href}
                                to={sub.href}
                                onClick={() => setOpen(false)}
                                className={`text-[10px] uppercase tracking-[0.2em] transition-colors ${
                                  subActive ? "text-[var(--gold)] font-bold" : "text-[var(--cream-muted)] hover:text-[var(--gold)]"
                                }`}
                              >
                                {sub.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </nav>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-16 pt-8 border-t border-white/5"
              >
                <div className="flex flex-col gap-6">
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    className="text-[10px] uppercase tracking-[0.4em] text-[var(--gold)] font-impact flex items-center gap-2 group"
                  >
                    <span>Start a Mission</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </Link>

                  <div className="flex justify-between items-center text-[8px] uppercase tracking-[0.5em] text-white/10 font-impact">
                    <span>© 2026 MERAKI SOLUTIONS</span>
                    <span>STUDIO • BHOPAL</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Subtle Texture */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.02] bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] -z-10" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Logo = () => (
  <div className="relative h-14 flex items-center group">
    <img
      src={siteMeta.logo}
      alt="Meraki Logo"
      className="h-full w-auto object-contain transition-all duration-700"
      onError={(e) => {
        e.target.style.display = 'none';
        e.target.parentElement.innerHTML = '<div class="font-display text-[var(--gold)] text-2xl tracking-[0.3em] group-hover:italic transition-all">MERAKI</div>';
      }}
    />
  </div>
);

export default Navbar;
